// ============================================================
// 登录弹窗组件 — 深色科技风
// ============================================================

import { useState, useEffect } from 'react'
import { Modal, Form, Input, Button, Typography, Checkbox } from 'antd'
import { message } from './Message'
import {
  UserOutlined,
  LockOutlined,
  ThunderboltOutlined,
  CloseOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
} from '@ant-design/icons'
import { useAuthStore } from '../stores/auth.store'
import { useDeviceContext } from './DeviceProvider'
import CaptchaInput from './CaptchaInput'

const { Text, Link } = Typography

// ─── Props ──────────────────────────────────────────────────

interface LoginModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
  initialInviteCode?: string
  defaultRegister?: boolean
}

// ─── 组件 ───────────────────────────────────────────────────

export default function LoginModal({ open, onClose, onSuccess, initialInviteCode, defaultRegister }: LoginModalProps) {
  const { isMobile } = useDeviceContext()
  const { login, register } = useAuthStore()
  const [form] = Form.useForm()
  const [isRegister, setIsRegister] = useState(false)
  const [loading, setLoading] = useState(false)
  const [captcha, setCaptcha] = useState<{ token: string; answer: string }>({ token: '', answer: '' })
  const [errorMsg, setErrorMsg] = useState('')
  const [agreed, setAgreed] = useState(false)

  // 打开时如果有初始邀请码，自动切换到注册并回填
  useEffect(() => {
    if (open) {
      if (defaultRegister || initialInviteCode) {
        setIsRegister(true)
      }
      if (initialInviteCode) {
        setTimeout(() => {
          form.setFieldsValue({ inviteCode: initialInviteCode })
        }, 100)
      }
    }
  }, [open, initialInviteCode, defaultRegister])

  const handleSubmit = async (values: any) => {
    console.log('handleSubmit 开始')
    setLoading(true)
    setErrorMsg('')
    try {
      if (isRegister) {
        if (!agreed) {
          setErrorMsg('请阅读并同意用户协议和隐私政策')
          setLoading(false)
          return
        }
        if (values.password !== values.confirmPassword) {
          setErrorMsg('两次输入的密码不一致')
          setLoading(false)
          return
        }
        if (!captcha.token || !captcha.answer) {
          setErrorMsg('请输入验证码')
          setLoading(false)
          return
        }
        await register(values.username, values.password, values.inviteCode, captcha.token, captcha.answer)
        message.success('注册成功')
      } else {
        console.log('调用 login 方法')
        await login(values.username, values.password)
        console.log('login 方法成功')
        message.success('登录成功')
      }
      form.resetFields()
      setIsRegister(false)
      setErrorMsg('')
      onSuccess?.()
      onClose()
    } catch (error: any) {
      console.log('login 方法失败，进入 catch 块:', error.message)
      let msg = ''
      if (error?.message) {
        msg = error.message
      } else if (typeof error === 'string') {
        msg = error
      } else {
        msg = isRegister ? '注册失败，请稍后重试' : '登录失败，请稍后重试'
      }
      console.log('设置错误消息:', msg)
      setErrorMsg(msg)
    } finally {
      console.log('handleSubmit finally')
      setLoading(false)
    }
  }

  const handleClose = () => {
    form.resetFields()
    setIsRegister(false)
    setErrorMsg('')
    setAgreed(false)
    onClose()
  }

  const inputStyle = {
    background: 'transparent',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    borderRadius: 12,
    height: 48,
    color: '#FFFFFF',
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      width={isMobile ? '92vw' : 400}
      closable={false}
      maskClosable={!loading}
      className="login-modal"
      styles={{
        mask: { backdropFilter: 'blur(4px)' },
        body: { padding: 0, overflow: 'hidden' },
        wrapper: { transition: 'none' },
        content: {
          borderRadius: 20,
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          overflow: 'hidden',
        },
      }}
    >
      {/* 关闭按钮 */}
      <button
        onClick={handleClose}
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: 32,
          height: 32,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s',
          zIndex: 10,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'
          e.currentTarget.style.transform = 'scale(1.1)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'
          e.currentTarget.style.transform = 'scale(1)'
        }}
      >
        <CloseOutlined style={{ color: '#A0A0B0', fontSize: 14 }} />
      </button>

      {/* 顶部 Logo + 标题 */}
      <div
        style={{
          padding: isMobile ? '40px 24px 24px' : '48px 32px 28px',
          textAlign: 'center',
        }}
      >
        {/* Logo */}
        <div
          style={{
            width: 64,
            height: 64,
            margin: '0 auto 16px',
            borderRadius: 16,
            background: 'linear-gradient(135deg, #8B5CF6, #EC4899)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(139, 92, 246, 0.3)',
          }}
        >
          <ThunderboltOutlined style={{ fontSize: 28, color: '#FFFFFF' }} />
        </div>

        {/* 标题 */}
        <h2
          style={{
            fontSize: isMobile ? 20 : 22,
            fontWeight: 700,
            color: '#FFFFFF',
            marginBottom: 4,
          }}
        >
          广发圩
        </h2>
        <p
          style={{
            fontSize: 13,
            color: '#6B6B80',
            marginBottom: 0,
          }}
        >
          登录后享受完整功能
        </p>
      </div>

      {/* 表单区域 */}
      <div
        style={{
          padding: isMobile ? '24px 20px 28px' : '28px 32px 32px',
        }}
      >
        {/* 错误提示 */}
        {errorMsg && (
          <div
            style={{
              padding: '10px 14px',
              marginBottom: 16,
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: 10,
              color: '#EF4444',
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span>{errorMsg}</span>
            <CloseOutlined
              onClick={() => setErrorMsg('')}
              style={{ cursor: 'pointer', fontSize: 12, color: '#EF4444' }}
            />
          </div>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
          style={{ marginTop: 0 }}
        >
          <Form.Item
            name="username"
            rules={[{ required: true, message: '请输入用户名' }]}
            style={{ marginBottom: 16 }}
          >
            <Input
              prefix={<UserOutlined style={{ color: '#6B6B80' }} />}
              placeholder="请输入用户名"
              style={inputStyle}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: '请输入密码' }]}
            style={{ marginBottom: isRegister ? 16 : 24 }}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: '#6B6B80' }} />}
              placeholder="请输入密码"
              style={inputStyle}
              iconRender={(visible) =>
                visible ? (
                  <EyeTwoTone twoToneColor="#8B5CF6" />
                ) : (
                  <EyeInvisibleOutlined style={{ color: '#6B6B80' }} />
                )
              }
            />
          </Form.Item>

          {isRegister && (
            <>
              <Form.Item
                name="confirmPassword"
                dependencies={['password']}
                rules={[
                  { required: true, message: '请确认密码' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve()
                      }
                      return Promise.reject(new Error('两次输入的密码不一致'))
                    },
                  }),
                ]}
                style={{ marginBottom: 16 }}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: '#6B6B80' }} />}
                  placeholder="请确认密码"
                  style={inputStyle}
                  iconRender={(visible) =>
                    visible ? (
                      <EyeTwoTone twoToneColor="#8B5CF6" />
                    ) : (
                      <EyeInvisibleOutlined style={{ color: '#6B6B80' }} />
                    )
                  }
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 16 }}>
                <CaptchaInput value={captcha} onChange={setCaptcha} compact />
              </Form.Item>

              <Form.Item name="inviteCode" style={{ marginBottom: 16 }}>
                <Input
                  placeholder="邀请码（选填）"
                  style={inputStyle}
                />
              </Form.Item>

              {/* 用户协议勾选 */}
              <div style={{ marginBottom: 24 }}>
                <Checkbox
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  style={{ color: '#6B6B80', fontSize: 12 }}
                >
                  <span style={{ color: '#6B6B80', fontSize: 12 }}>
                    我已阅读并同意{' '}
                    <a
                      href="/agreement"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#A78BFA' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      《用户协议》
                    </a>
                    和{' '}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#A78BFA' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      《隐私政策》
                    </a>
                  </span>
                </Checkbox>
              </div>
            </>
          )}

          <Form.Item style={{ marginBottom: 12 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              style={{
                height: 48,
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #8B5CF6, #EC4899)',
                border: 'none',
                boxShadow: '0 8px 24px rgba(139, 92, 246, 0.4)',
              }}
            >
              {isRegister ? '注 册' : '登 录'}
            </Button>
          </Form.Item>
        </Form>

        {/* 切换登录/注册 */}
        <div style={{ textAlign: 'center' }}>
          <Text style={{ color: '#6B6B80', fontSize: 13 }}>
            {isRegister ? '已有账号？' : '还没有账号？'}
            <Link
              onClick={() => {
                setIsRegister(!isRegister)
                form.resetFields()
                setErrorMsg('')
                setAgreed(false)
              }}
              style={{ color: '#A78BFA', fontWeight: 500, marginLeft: 4 }}
            >
              {isRegister ? '立即登录' : '立即注册'}
            </Link>
          </Text>
        </div>
      </div>
    </Modal>
  )
}

// ─── 全局触发函数 ───────────────────────────────────────────

let globalLoginModalCallback: (() => void) | null = null

/** 设置登录弹窗成功回调 */
export function setLoginModalCallback(callback: () => void) {
  globalLoginModalCallback = callback
}

/** 触发登录弹窗 */
export function triggerLoginModal(callback?: () => void, options?: { inviteCode?: string; defaultRegister?: boolean }) {
  if (callback) {
    globalLoginModalCallback = callback
  }
  window.dispatchEvent(new CustomEvent('login-modal:open', { detail: options }))
}

/** 清除登录弹窗回调 */
export function clearLoginModalCallback() {
  globalLoginModalCallback = null
}

/** 获取登录弹窗回调 */
export function getLoginModalCallback() {
  return globalLoginModalCallback
}
